import { useEffect, useState } from 'react';

// Hero en pipeline : les trois promesses a gauche, l ecran qui les prouve a droite.
// Chaque etape est son propre declencheur — on ne separe plus le discours de la preuve.
const STEPS = [
  {
    id: 'cv',
    marker: '01',
    verb: 'Analyser vos',
    object: 'CV',
    path: 'talents',
    image: '/images/talents-mockup.png',
    caption: 'Compétences, séniorité, expérience et disponibilité extraites de chaque CV.',
    alt: 'Liste des talents qualifiés avec rôle, top compétences et expérience',
  },
  {
    id: 'ao',
    marker: '02',
    verb: 'Structurer vos',
    object: 'appels d’offres',
    path: 'missions/analyse',
    image: '/images/bento-risks.png',
    caption: 'Deal breakers, TJM, séniorité et mode de travail sortis de la fiche de poste.',
    alt: 'Analyse d’une mission : deal breakers identifiés et informations stratégiques',
  },
  {
    id: 'profils',
    marker: '03',
    verb: 'Trouver vos meilleurs',
    object: 'profils',
    path: 'matching',
    image: '/images/bento-matching.png',
    caption: 'Score, analyse, critères MUST/NICE couverts et statut, candidat par candidat.',
    alt: 'Résultats du matching : score, analyse IA, critères couverts et statut par candidat',
  },
];

// Escalier discret : la colonne est etroite, l indentation reste legere.
const INDENT = ['', 'xl:ml-4', 'xl:ml-8'];

const ROTATION_MS = 5000;

export default function HeroPipeline({ onImageClick }: { onImageClick?: (src: string) => void }) {
  const [active, setActive] = useState(0);
  // La rotation s arrete definitivement des que l utilisateur prend la main.
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => setActive(i => (i + 1) % STEPS.length), ROTATION_MS);
    return () => window.clearInterval(timer);
  }, [auto]);

  const select = (i: number) => {
    setAuto(false);
    setActive(i);
  };

  const current = STEPS[active];

  return (
    <div className="grid gap-12 xl:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] 2xl:grid-cols-[minmax(0,44rem)_minmax(0,1fr)] xl:gap-14 xl:items-center">
      {/* Colonne gauche : la promesse */}
      <div>
        <span
          className="cascade-item font-display mb-8 flex items-center gap-4 text-sm font-bold uppercase tracking-[0.15em]"
          style={{ animationDelay: '0ms' }}
        >
          <span className="h-px w-10 bg-amber-500/60" aria-hidden="true" />
          Revert<span className="text-amber-500">AI</span>
        </span>

        <h1 className="font-display">
          <span role="tablist" aria-label="Étapes du produit" className="block">
          {STEPS.map((step, i) => {
            const isActive = i === active;
            return (
              <button
                key={step.id}
                role="tab"
                aria-selected={isActive}
                aria-controls="hero-screen"
                onClick={() => select(i)}
                style={{ animationDelay: `${180 + i * 110}ms` }}
                className={`cascade-item group block w-full text-left ${INDENT[i]}`}
              >
                <span className="flex items-baseline gap-4 text-3xl md:text-4xl xl:text-3xl 2xl:text-[2.5rem] font-bold tracking-tight leading-[1.2] transition-colors duration-300 xl:whitespace-nowrap">
                  <span
                    className={`font-mono-ui shrink-0 text-sm font-medium transition-colors duration-300 ${
                      isActive ? 'text-amber-500' : 'text-slate-600 group-hover:text-amber-500/60'
                    }`}
                    aria-hidden="true"
                  >
                    {step.marker}
                  </span>
                  <span className={isActive ? 'text-slate-400' : 'text-slate-700 group-hover:text-slate-500'}>
                    {step.verb}{' '}
                    <span className={isActive ? 'text-white' : 'text-slate-600 group-hover:text-slate-400'}>
                      {step.object}
                    </span>
                  </span>
                </span>

                {/* Filet de progression : se remplit pendant la rotation, statique une fois la main prise */}
                <span className={"mt-2 mb-4 ml-9 block h-px overflow-hidden " + (isActive ? "bg-slate-800" : "bg-transparent")} aria-hidden="true">
                  {isActive && (
                    <span
                      key={auto ? `auto-${active}` : 'manual'}
                      className={`block h-full bg-amber-500 ${auto ? 'hero-progress' : 'w-full'}`}
                    />
                  )}
                </span>
              </button>
            );
          })}
          </span>
        </h1>

        <p
          className="cascade-item mt-8 border-l-2 border-amber-500/50 pl-5 text-lg 2xl:text-xl leading-relaxed text-slate-400 xl:whitespace-nowrap"
          style={{ animationDelay: '560ms' }}
        >
          <span className="font-semibold text-white">Présenter des arguments</span>, pas juste un nom.
        </p>
      </div>

      {/* Colonne droite : la preuve */}
      <div className="cascade-item" style={{ animationDelay: '300ms' }}>
        <div
          id="hero-screen"
          role="tabpanel"
          className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/70"
        >
          <div className="flex h-10 items-center gap-2 border-b border-slate-700/50 bg-slate-800/80 px-4 backdrop-blur-md">
            <div className="h-3 w-3 rounded-full bg-red-500/60" />
            <div className="h-3 w-3 rounded-full bg-yellow-500/60" />
            <div className="h-3 w-3 rounded-full bg-green-500/60" />
            <div className="mx-4 flex max-w-xs flex-1 items-center justify-center truncate rounded-md bg-slate-700/50 px-3 py-1 text-xs font-medium text-slate-400">
              app.revertai.io/{current.path}
            </div>
          </div>

          {/* aspect-[8/5] = ratio natif des captures pleine app : aucun rognage sur celles-ci */}
          <div className="relative aspect-[8/5] bg-slate-100">
            {STEPS.map((step, i) => (
              <img
                key={step.id}
                src={step.image}
                alt={step.alt}
                loading="eager"
                fetchPriority={i === 0 ? 'high' : 'low'}
                decoding="async"
                aria-hidden={i !== active}
                onClick={onImageClick ? () => onImageClick(step.image) : undefined}
                className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500 ${
                  i === active ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
                }${onImageClick ? ' cursor-zoom-in' : ''}`}
              />
            ))}
          </div>
        </div>

        <p className="font-mono-ui mt-4 pl-1 text-xs leading-relaxed text-slate-500">{current.caption}</p>
      </div>
    </div>
  );
}
